package diana.soleil.design.iraniancanadienbazzar.core.devmenu

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import diana.soleil.design.iraniancanadienbazzar.core.common.AppBuildInfo
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SectionHeader
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SettingsRow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.Res
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_build_debug
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_build_release
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_build_type
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_env
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_force_crash
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_force_crash_sub
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_no_tools
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_note
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_section_diagnostics
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_section_tools
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.resources.devmenu_version
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

/**
 * Contents of the debug-only Developer Menu (host it in an `AppBottomSheet` from the Profile screen's
 * 5-tap build-number gesture — see docs/DEVELOPER_MENU.md). Shows live build diagnostics, a baseline
 * "force crash" tool, and every feature-contributed [DevTool] from the [DevMenuRegistry].
 *
 * Debug-only surface, so its copy is English-only by the doc's explicit dispensation (still resourced,
 * no hardcoded literals). Injects [AppBuildInfo] + [DevMenuRegistry] from DI.
 */
@Composable
fun DeveloperMenuContent(
    modifier: Modifier = Modifier,
    buildInfo: AppBuildInfo = koinInject(),
    registry: DevMenuRegistry = koinInject(),
) {
    val tools by registry.tools.collectAsState()

    // Scrollable: the tool list outgrows the sheet on shorter screens (e.g. iPhone 16 Pro clipped
    // the 5th tool with no way to reach it).
    Column(
        modifier = modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Text(
            text = stringResource(Res.string.devmenu_note),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
        )

        // Diagnostics
        SectionHeader(title = stringResource(Res.string.devmenu_section_diagnostics))
        AppCard(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                DiagnosticRow(stringResource(Res.string.devmenu_env), buildInfo.firebaseEnv)
                DiagnosticRow(
                    stringResource(Res.string.devmenu_version),
                    "${buildInfo.versionName} (${buildInfo.versionCode})",
                )
                DiagnosticRow(
                    stringResource(Res.string.devmenu_build_type),
                    stringResource(
                        if (buildInfo.isDebug) Res.string.devmenu_build_debug else Res.string.devmenu_build_release,
                    ),
                )
            }
        }

        // Tools
        SectionHeader(title = stringResource(Res.string.devmenu_section_tools))
        SettingsRow(
            iconName = "bug_report",
            title = stringResource(Res.string.devmenu_force_crash),
            subtitle = stringResource(Res.string.devmenu_force_crash_sub),
            onClick = { throw RuntimeException("Developer Menu: forced test crash") },
        )
        // Feature-contributed tools.
        tools.forEach { tool ->
            SettingsRow(
                iconName = tool.iconName,
                title = tool.title,
                subtitle = tool.subtitle,
                onClick = tool.onInvoke,
            )
        }
        if (tools.isEmpty()) {
            Text(
                text = stringResource(Res.string.devmenu_no_tools),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant.copy(alpha = 0.6f),
            )
        }
    }
}

@Composable
private fun DiagnosticRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(text = label, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurfaceVariant)
        Text(
            text = value,
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onSurface,
            textAlign = TextAlign.End,
        )
    }
}
